import React from 'react';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
  WithStyles,
} from '@material-ui/core/styles';
import MuiDialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import colors from '../../../../../themes/main-colors';
import { useIsMobile } from '../../../../../util/style-utils';
import { Box, Grid, Slide } from '@material-ui/core';
import { TransitionProps } from '@material-ui/core/transitions';
import clsx from 'clsx';

interface CustomDialogProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  titleIcon?: React.ReactNode;
  titleBgColor?: string;
  titleTextColor?: string;
  dialogWidth?: number | string; //e.g '800px' or 800
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paperMobile: {
      margin: 0,
      bottom: 0,
      position: 'fixed',
      maxHeight: '90vh',
      width: '100%',
      maxWidth: 900,
      [theme.breakpoints.down('sm')]: {
        height: 'inherit',
      },
    },
  }),
);

interface TitleStyleProps {
  titleBgColor?: string;
  titleTextColor?: string;
}
// --- Title ---
const titleStyles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(3.75),
      backgroundColor: (props: TitleStyleProps) =>
        props.titleBgColor || colors.starlightBlue,
    },
    titleContent: {
      display: 'flex',
      gap: '10px',
      color: (props: TitleStyleProps) => props.titleTextColor || colors.white,
      fontWeight: 600,
    },
    closeButton: {
      color: (props: TitleStyleProps) => props.titleTextColor || colors.white,
      padding: 0,
    },
  });

interface DialogTitleProps
  extends WithStyles<typeof titleStyles>,
    TitleStyleProps {
  id?: string;
  children: React.ReactNode;
  onClose?: () => void;
  icon?: React.ReactNode;
}

const DialogTitle = withStyles(titleStyles, { withTheme: true })(
  (props: DialogTitleProps) => {
    const {
      children,
      classes,
      icon,
      onClose,
      titleBgColor,
      titleTextColor,
      ...other
    } = props;
    return (
      <MuiDialogTitle disableTypography className={classes.root} {...other}>
        <Grid
          container
          justify="space-between"
          wrap="nowrap"
          alignItems="center"
        >
          <Grid item container spacing={1}>
            {icon && (
              <Grid item style={{ paddingRight: 10 }}>
                {icon}
              </Grid>
            )}
            <Box alignItems="center" display="flex">
              <Typography variant="h2" className={classes.titleContent}>
                {children}
              </Typography>
            </Box>
          </Grid>
          {onClose && (
            <Grid item>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={onClose}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </MuiDialogTitle>
    );
  },
);

// ---------- Dialog ----------
const Dialog = withStyles({
  paper: {
    borderRadius: 0,
  },
})(MuiDialog);

// ---------- Content ----------
const DialogContent = withStyles((theme: Theme) => ({
  root: { padding: 0, backgroundColor: colors.riverBlue },
}))(MuiDialogContent);

// ---------- Actions ----------
const DialogActions = withStyles((theme: Theme) => ({
  root: { margin: 0, padding: theme.spacing(1) },
}))(MuiDialogActions);

const BottomSlide = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const CustomDialog: React.FC<CustomDialogProps> = ({
  open,
  onClose,
  titleIcon,
  title,
  actions,
  children,
  titleBgColor,
  titleTextColor,
  dialogWidth = 600,
}) => {
  const isMobile = useIsMobile();
  const classes = useStyles();

  return (
    <>
      {open && isMobile && (
        <style type="text/css">
          {`
      .MuiAlert-filledError {
        position: fixed;
        z-index: 99999;
        top: 0;
      }
    `}
        </style>
      )}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={isMobile ? undefined : 'md'}
        TransitionComponent={isMobile ? BottomSlide : undefined}
        PaperProps={{
          className: clsx({ [classes.paperMobile]: isMobile }),
          style: dialogWidth && !isMobile ? { width: dialogWidth } : undefined,
        }}
        disableRestoreFocus
      >
        <DialogTitle
          onClose={onClose}
          icon={titleIcon}
          titleBgColor={titleBgColor}
          titleTextColor={titleTextColor}
        >
          {title}
        </DialogTitle>
        <DialogContent>{children}</DialogContent>
        {actions && <DialogActions>{actions}</DialogActions>}
      </Dialog>
    </>
  );
};
